/**
 * WorkflowService
 * 
 * This service handles all workflow-related operations in the application.
 * It provides methods for managing workflows, including creation, retrieval,
 * and updating of workflow assignments. The service supports operations for
 * both system-wide workflows and individual workflow management, with
 * capabilities for updating matter statuses and workflow assignments.
 * 
 * @class
 */

import HttpService from './HttpService';
import {
  pathWorkflows,
  pathWorkflowId,
  pathMatterStatus,
  pathMatterWorkflow,
} from '@frontend/__settings__/septSoftwarePaths';
import type { HttpError } from './HttpService';
import type {
  GetWorkflowsResponse,
  CreateWorkflowRequest,
  CreateWorkflowResponse,
  GetWorkflowResponse,
  UpdateMatterStatusRequest,
  UpdateMatterStatusResponse,
  UpdateMatterWorkflowRequest,
  UpdateMatterWorkflowResponse,
  DeleteWorkflowResponse,
  UpdateWorkflowRequest,
  UpdateWorkflowResponse,
} from '@frontend/Models/Workflow/WorkflowRequestResponseModels';
import type { Workflow } from '@frontend/Models/Workflow/WorkflowModels';

const http = new HttpService();

class WorkflowService {
    /**
     * Retrieves all workflows from the system
     * 
    * @returns {Promise<Workflow[]>} Array of workflow objects
     * @throws {Error} If the API request fails
     */
    static async getWorkflows(): Promise<Workflow[]> {
       const res = await http.get<GetWorkflowsResponse>(pathWorkflows);
       return res.data;
    }

    /**
     * Creates a new workflow in the system
     * 
     * @param {CreateWorkflowRequest} workflowData - Workflow data including name, milestones, and other configuration
     * @returns {Promise<CreateWorkflowResponse>} Created workflow object
     * @throws {Error} If the API request fails
     */
    static async addWorkflow(workflowData: CreateWorkflowRequest): Promise<CreateWorkflowResponse> {
      return http.post<CreateWorkflowResponse>(pathWorkflows, workflowData);
    }

    /**
     * Retrieves a specific workflow by its ID
     * 
     * @param {string} workflowId - ID of the workflow to fetch
    * @returns {Promise<Workflow|null>} Workflow object with name and milestones, or null if not found
     * @throws {Error} If the API request fails
     */
    static async fetchAssignedWorkflow(workflowId: string): Promise<Workflow | null> {
      if (!workflowId) {
          console.error('No workflow ID provided.');
          return null;
      }
      try {
        const res = await http.get<GetWorkflowResponse>(pathWorkflowId(workflowId));
        return res.data;
      } catch (err) {
        if ((err as HttpError).status === 404) {
          return null;
        }
        throw err;
      }
    }
    
    /**
     * Updates the status of a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @param {string} status - New status to set for the matter
     * @returns {Promise<UpdateMatterStatusResponse>} Updated matter object
     * @throws {Error} If the API request fails
     */
    static async updateMatterStatus(matterId: string, status: string): Promise<UpdateMatterStatusResponse> {
      const request: UpdateMatterStatusRequest = { status };
      return http.put<UpdateMatterStatusResponse>(pathMatterStatus(matterId), request);
    }
    
    /**
     * Updates the workflow assignment for a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @param {string} workflowId - ID of the workflow to assign
     * @returns {Promise<UpdateMatterWorkflowResponse>} Updated matter object with new workflow assignment
     * @throws {Error} If the API request fails
     */
    static async updateMatterWorkflow(matterId: string, workflowId: string): Promise<UpdateMatterWorkflowResponse> {
      const request: UpdateMatterWorkflowRequest = { workflowid: workflowId };
      return http.put<UpdateMatterWorkflowResponse>(pathMatterWorkflow(matterId), request);
    }

    /**
     * Deletes a workflow from the system
     * 
     * @param {string} workflowId - ID of the workflow to delete
     * @returns {Promise<DeleteWorkflowResponse>} Deletion confirmation
     * @throws {Error} If the API request fails
     */
    static async deleteWorkflow(workflowId: string): Promise<DeleteWorkflowResponse> {
        if (!workflowId) {
            throw new Error('No workflow ID provided.');
        }
        return http.delete<DeleteWorkflowResponse>(pathWorkflowId(workflowId));
    }

    /**
     * Updates an existing workflow in the system
     * 
     * @param {string} workflowId - ID of the workflow to update
     * @param {UpdateWorkflowRequest} workflowData - Updated workflow data
     * @returns {Promise<UpdateWorkflowResponse>} Updated workflow object
     * @throws {Error} If the API request fails
     */
    static async updateWorkflow(workflowId: string, workflowData: UpdateWorkflowRequest): Promise<UpdateWorkflowResponse> {
        if (!workflowId) {
            throw new Error('No workflow ID provided.');
        }
        const data = await http.put<UpdateWorkflowResponse>(pathWorkflowId(workflowId), workflowData);
        return data;
    }
}

export default WorkflowService;
